import { Component, effect, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthorizationService, ConnectionService, Samv2Module, TemplateService, UserService } from '@aubert-et-duval/samv2';
import { PERMISSIONS } from './shared/guards/permissions';
import { TouaregControlTriggerComponent } from './modules/touareg-control/components/touareg-control-trigger/touareg-control-trigger.component';

@Component({
  selector: 'app-root',
  imports: [Samv2Module, RouterLink, TouaregControlTriggerComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  url = signal('');
  isConnected = signal(true);
  readonly canAccessAdmin = signal(false);
  readonly canPair = signal(false);
  readonly canUnPair = signal(false);
  readonly canSeeTagsPaired = signal(false);
  readonly canManageDemandesApproTags = signal(false);
  readonly searchExpedition = signal(false);
  readonly canPrintPackageLabel = signal(false);
  readonly canAccessArticles = signal(false);
  readonly canAccessComments = signal(false);
  readonly managePriority = signal(false);
  readonly canAccessInterruptReasons = signal(false);

  constructor(
    private readonly templateService: TemplateService,
    private readonly userService: UserService,
    private readonly authService: AuthorizationService,
    private readonly router: Router,
    connectionService: ConnectionService,
  ) {
    effect(() => {
      this.isConnected.set(connectionService.connectionStatusListener()());
    });

    // Réagir aux changements d'utilisateur et configurer le template
    effect(() => {
      const user = this.userService.currentUser$();
      if (user) {
        // Un utilisateur est connecté → afficher les menus
        this.templateService.setTemplateParameters({
          ShowMenuButton: true,
          NavBarVisible: true,
        });
        this.loadPermissions();
      } else {
        this.resetPermissions();
      }
    });

    this.router.events.subscribe(() => {
      this.url.set(this.router.url);
    });
    this.url.set(this.router.url);
    templateService.setTemplateParameters({
      ShowMenuButton: true,
      NavBarVisible: true,
    });
  }

  ngOnInit(): void {
    this.userService.checkLoginAndSetCurrentUser();
  }

  navigateHome(): void {
    void this.router.navigateByUrl('/home');
  }

  private loadPermissions(): void {
    Promise.all([
      Promise.all(PERMISSIONS.ADMIN.map(p => this.authService.hasPermission(p))),
      Promise.all(PERMISSIONS.PAIRING.map(p => this.authService.hasPermission(p))),
      this.authService.hasPermission(PERMISSIONS.EXPEDITION.SEARCH),
      Promise.all(PERMISSIONS.ARTICLES.map(p => this.authService.hasPermission(p))),
      Promise.all(PERMISSIONS.COMMENTS.map(p => this.authService.hasPermission(p))),
      Promise.all(PERMISSIONS.INTERRUPT_REASONS.map(p => this.authService.hasPermission(p))),
      Promise.all(PERMISSIONS.PRIORITIES.map(p => this.authService.hasPermission(p))),
    ]).then(([admin, pairing, searchExpedition, articles, comments, interruptReasons, priorities]) => {
      const canAccessArticles = articles.some(Boolean);
      const canAccessComments = comments.some(Boolean);
      const canAccessInterruptReasons = interruptReasons.some(Boolean);
      const managePriority = priorities.some(Boolean);

      const hasCustomSettings =
        canAccessArticles ||
        canAccessComments ||
        canAccessInterruptReasons ||
        managePriority ||
        searchExpedition;

      this.canAccessAdmin.set(admin.some(Boolean) || hasCustomSettings);
      this.canPair.set(pairing[0]);
      this.canUnPair.set(pairing[1]);
      this.canSeeTagsPaired.set(pairing[2]);
      this.canManageDemandesApproTags.set(pairing[3]);
      this.searchExpedition.set(searchExpedition);
      this.canPrintPackageLabel.set(searchExpedition);
      this.canAccessArticles.set(canAccessArticles);
      this.canAccessComments.set(canAccessComments);
      this.canAccessInterruptReasons.set(canAccessInterruptReasons);
      this.managePriority.set(managePriority);
    });
  }

  private resetPermissions(): void {
    this.canAccessAdmin.set(false);
    this.canPair.set(false);
    this.canUnPair.set(false);
    this.canSeeTagsPaired.set(false);
    this.canManageDemandesApproTags.set(false);
    this.searchExpedition.set(false);
    this.canPrintPackageLabel.set(false);
    this.canAccessArticles.set(false);
    this.canAccessComments.set(false);
    this.canAccessInterruptReasons.set(false);
    this.managePriority.set(false);
  }
}
